// ─── Dokumentsummen, Zielpreis, § 35a ───────────────────────────────────────

import { d, summe } from './dezimal.js';

// Eventual- und Wahlpositionen zählen nicht zur Summe.
export function zaehltZurSumme(pos) {
  const typ = pos.position_typ || pos.typ || 'standard';
  return typ === 'standard' || typ === 'nachlass' || typ === 'rundung';
}

/**
 * @param positionen [{ gp, menge, ust_satz?, position_typ?, gruppe?,
 *                      ek_summe_je_einheit?, arbeitskosten_je_einheit? }]
 * @param optionen { ust_satz = 19, reverse_charge = false }
 */
export function berechneDokument(positionen, optionen = {}) {
  const standardSatz = d(optionen.ust_satz ?? 19);
  const relevant = (positionen || []).filter(zaehltZurSumme);

  // Titelsummen in Reihenfolge des ersten Auftretens
  const titel = [];
  const titelIndex = new Map();
  for (const p of relevant) {
    const name = p.gruppe ?? '';
    if (!titelIndex.has(name)) { titelIndex.set(name, titel.length); titel.push({ name, summe: d(0) }); }
    const t = titel[titelIndex.get(name)];
    t.summe = t.summe.plus(d(p.gp));
  }

  const netto = summe(relevant.map((p) => d(p.gp)));

  // USt auf Summenebene je Steuersatz (XRechnung-konform), nicht pro Zeile
  const jeSatz = new Map();
  for (const p of relevant) {
    const satz = optionen.reverse_charge ? '0' : d(p.ust_satz ?? standardSatz).alsText(2);
    jeSatz.set(satz, (jeSatz.get(satz) || d(0)).plus(d(p.gp)));
  }
  const steuern = [...jeSatz.entries()].map(([satz, basis]) => ({
    satz: d(satz),
    basis,
    betrag: basis.mal(d(satz)).durch(100).runde(2),
  }));
  const ust = summe(steuern.map((s) => s.betrag));
  const brutto = netto.plus(ust);

  const gesamtEk = summe(relevant
    .filter((p) => p.ek_summe_je_einheit !== undefined && p.ek_summe_je_einheit !== null)
    .map((p) => d(p.ek_summe_je_einheit).mal(d(p.menge ?? 1))));
  const hatEk = relevant.some((p) => p.ek_summe_je_einheit !== undefined && p.ek_summe_je_einheit !== null);
  const zuschlagBetrag = hatEk ? netto.minus(gesamtEk) : null;
  const zuschlagProzent = hatEk && !gesamtEk.istNull() ? zuschlagBetrag.durch(gesamtEk).mal(100) : null;

  return {
    titel,
    netto,
    steuern,
    ust,
    brutto,
    gesamt_ek: hatEk ? gesamtEk : null,
    zuschlag_betrag: zuschlagBetrag,
    zuschlag_prozent: zuschlagProzent,
    reverse_charge: !!optionen.reverse_charge,
  };
}

/**
 * Zielpreis setzen.
 * modus 'NACHLASS_AUSWEISEN': Nachlasszeile = Ziel − Netto, Positionen bleiben.
 * modus 'EINRECHNEN': alle VK-Anteile mit f = Ziel / Netto skalieren, EP neu runden,
 *                     Restdifferenz als Rundungsausgleich.
 * @param positionen wie berechneDokument, zusätzlich vk_summe_je_einheit (ungerundet)
 */
export function zielpreis(positionen, ziel, modus, optionen = {}) {
  const vorher = berechneDokument(positionen, optionen);
  const zielNetto = optionen.ziel_ist_brutto
    ? d(ziel).durch(d(1).plus(d(optionen.ust_satz ?? 19).durch(100))).runde(2)
    : d(ziel).runde(2);

  const warnungen = [];
  if (vorher.gesamt_ek && zielNetto.kleinerAls(vorher.gesamt_ek)) {
    warnungen.push({
      art: 'VERLUST',
      text: `Der Zielpreis ${zielNetto.alsText(2)} € liegt unter euren Kosten von ${vorher.gesamt_ek.alsText(2)} €.`,
    });
  }

  if (modus === 'NACHLASS_AUSWEISEN') {
    const nachlass = zielNetto.minus(vorher.netto);
    const neu = [...positionen, { position_typ: 'nachlass', beschreibung: 'Nachlass', menge: 1, gp: nachlass, ep: nachlass }];
    return { modus, faktor: null, nachlass, positionen: neu, dokument: berechneDokument(neu, optionen), warnungen, vorher };
  }

  if (vorher.netto.istNull()) throw new Error('Ohne Nettosumme lässt sich kein Zielpreis einrechnen.');
  const f = zielNetto.durch(vorher.netto);
  const neu = positionen.map((p) => {
    if (!zaehltZurSumme(p) || p.position_typ === 'nachlass' || p.position_typ === 'rundung') return p;
    const basis = d(p.vk_summe_je_einheit ?? p.ep);
    const ep = basis.mal(f).runde(2);
    const gp = ep.mal(d(p.menge ?? 1)).runde(2);
    return {
      ...p,
      ep, gp,
      vk_summe_je_einheit: basis.mal(f),
      arbeitskosten_je_einheit: p.arbeitskosten_je_einheit !== undefined ? d(p.arbeitskosten_je_einheit).mal(f) : p.arbeitskosten_je_einheit,
    };
  });
  const zwischen = berechneDokument(neu, optionen);
  const ausgleich = zielNetto.minus(zwischen.netto);
  if (!ausgleich.istNull()) {
    neu.push({ position_typ: 'rundung', beschreibung: 'Rundungsausgleich', menge: 1, gp: ausgleich, ep: ausgleich });
  }
  return { modus, faktor: f, nachlass: null, ausgleich, positionen: neu, dokument: berechneDokument(neu, optionen), warnungen, vorher };
}

// Arbeitskostenanteil nach § 35a EStG — nur Lohn- und Gerätekosten (flagbar).
export function arbeitskosten35a(positionen, optionen = {}) {
  const relevant = (positionen || []).filter((p) => (p.position_typ || 'standard') === 'standard');
  const netto = summe(relevant.map((p) => d(p.arbeitskosten_je_einheit ?? 0).mal(d(p.menge ?? 1))));
  const satz = d(optionen.ust_satz ?? 19);
  const brutto = netto.mal(d(1).plus(satz.durch(100))).runde(2);
  return { netto: netto.runde(2), brutto };
}
